import {
    CheckCircle2,
    CircleDashed,
    Clock,
    HelpCircle,
    Pause,
    XCircle,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';

type StatusType =
    | 'completed'
    | 'in-progress'
    | 'on-hold'
    | 'not-started'
    | boolean
    | undefined;

type StatusBadgeProps = {
    status: StatusType;
    text?: string;
    showIcon?: boolean;
    size?: 'sm' | 'md' | 'lg';
};

const StatusBadge = ({
    status,
    text,
    showIcon = true,
    size = 'md',
}: StatusBadgeProps) => {
    const getStatusColor = (status: StatusType): string => {
        if (typeof status === 'string') {
            switch (status.toLowerCase()) {
                case 'completed':
                    return 'bg-green-500';
                case 'in-progress':
                    return 'bg-blue-500';
                case 'on-hold':
                    return 'bg-orange-500';
                case 'not-started':
                    return 'bg-slate-400';
                default:
                    return 'bg-gray-500';
            }
        }
        // Legacy boolean support; TODO: remove in future
        switch (status) {
            case true:
                return 'bg-green-500';
            case false:
                return 'bg-red-500';
            default:
                return 'bg-yellow-500';
        }
    };

    const getStatusIcon = (status: StatusType) => {
        const iconSizeClass =
            size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-6 w-6';

        if (typeof status === 'string') {
            switch (status.toLowerCase()) {
                case 'completed':
                    return (
                        <CheckCircle2
                            className={`${iconSizeClass} text-green-500`}
                        />
                    );
                case 'in-progress':
                    return (
                        <Clock className={`${iconSizeClass} text-blue-500`} />
                    );
                case 'on-hold':
                    return (
                        <Pause className={`${iconSizeClass} text-orange-500`} />
                    );
                case 'not-started':
                    return (
                        <CircleDashed
                            className={`${iconSizeClass} text-slate-400`}
                        />
                    );
                default:
                    return (
                        <HelpCircle
                            className={`${iconSizeClass} text-gray-500`}
                        />
                    );
            }
        }
        // Legacy boolean support
        switch (status) {
            case true:
                return (
                    <CheckCircle2
                        className={`${iconSizeClass} text-green-500`}
                    />
                );
            case false:
                return <XCircle className={`${iconSizeClass} text-red-500`} />;
            default:
                return (
                    <HelpCircle
                        className={`${iconSizeClass} text-yellow-500`}
                    />
                );
        }
    };

    const getStatusText = (status: StatusType): string => {
        if (typeof status === 'string') {
            switch (status.toLowerCase()) {
                case 'completed':
                    return 'Completed';
                case 'in-progress':
                    return 'In Progress';
                case 'on-hold':
                    return 'On Hold';
                case 'not-started':
                    return 'Not Started';
                default:
                    return status;
            }
        }
        // Legacy boolean support
        switch (status) {
            case true:
                return 'Operational';
            case false:
                return 'Down';
            default:
                return 'Unknown';
        }
    };

    const badgeSizeClass =
        size === 'sm'
            ? 'text-xs px-1.5 py-0.5'
            : size === 'md'
              ? 'text-sm px-2 py-1'
              : 'text-base px-3 py-1.5';

    return (
        <div className="flex items-center space-x-2" data-testid="StatusBadge">
            {showIcon && getStatusIcon(status)}
            <Badge
                variant="secondary"
                className={`${getStatusColor(status)} ${badgeSizeClass} text-white border-0`}
            >
                {text || getStatusText(status)}
            </Badge>
        </div>
    );
};

export default StatusBadge;
